import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

function Madurai() {
    const [openFaq, setOpenFaq] = useState(null);
    const navigate = useNavigate();

    const tripCards = [
        {
            icon: "🏛️",
            title: "Interesting Facts About Madurai",
            desc: "Madurai is one of the oldest continuously inhabited cities in India, often called the Athens of the East. The city is famous for the historic Meenakshi Amman Temple and its deep cultural roots in Tamil literature, art, and tradition."
        },
        {
            icon: "🍽️",
            title: "Culinary Delights",
            desc: "Madurai is known as a 24/7 food city and is famous for its vibrant street food culture. Must-try dishes include Jigarthanda, Kari Dosa, Bun Parotta, and the city’s signature soft idlies."
        },
        {
            icon: "🎉",
            title: "Famous Festivals in Madurai",
            desc: "Madurai celebrates many grand festivals, especially the famous Chithirai Festival held at the Meenakshi Amman Temple along with Navaratri and Pongal."
        },
        {
            icon: "🌤️",
            title: "Best Time to Visit Madurai",
            desc: "The best time to visit Madurai is from October to March when the weather is pleasant for temple visits and sightseeing."
        }
    ];

    const faqs = [
        {
            q: "How can I book a self drive car in Madurai with Wow carz?",
            a: "Booking is simple: visit the website or app, choose your car, select a package (hourly/daily/weekly or 100 km trip), and confirm. You'll receive a booking confirmation and pickup details.",
        },
        {
            q: "What documents are required to rent a car in Madurai?",
            a: "You need a valid driving license (minimum age 21 and at least 1 year of driving history) and a government-issued ID (Aadhar or Passport).",
        },
        {
            q: "Can I rent a car for a short trip within the city?",
            a: "Yes — Wow carz offers hourly, daily and monthly packages suitable for city travel, shopping, meetings, and short outings.",
        },
        {
            q: "Is it possible to take the rental car outside Madurai?",
            a: "Yes — choose the appropriate outstation package to drive to nearby destinations like Nandi Hills, Mysore, Coorg and more. Check package details for kilometer limits and charges.",
        },
        {
            q: "Why should I choose Wow carz over other car rental services in Madurai?",
            a: "Wow carz provides hassle-free booking, competitive pricing, well-maintained cars and 24/7 support — giving you freedom to travel at your own pace.",
        },
        {
            q: "Are Wow carz rentals available for both short and long durations?",
            a: "Yes — you can book hourly packages for short trips or daily/weekly rentals for long trips and weekend getaways.",
        },
    ];

    const places = [
        {
            image: "/assets/rameswaram.jpg",
            title: "Rameswaram",
            desc: "Rameswaram is one of the holiest pilgrimage destinations in India, famous for the Ramanathaswamy Temple and the historic Pamban Bridge. The island town offers beautiful sea views and deep spiritual significance."
        },
        {
            image: "/assets/kodaikanal.jpg",
            title: "Kodaikanal Hill Station",
            desc: "Kodaikanal is a famous hill station near Madurai known for its cool climate, misty mountains, waterfalls, and scenic viewpoints. It is a perfect destination for nature lovers and weekend road trips.",
        },

        {
            image: "/assets/samanar-hills.jpg",
            title: "Samanar Hills",
            desc: "Samanar Hills is an ancient historical site known for Jain caves, inscriptions, and stone carvings dating back centuries. It is a peaceful trekking spot offering panoramic views of Madurai."
        },
        {
            image: "/assets/megamalai.jpg",
            title: "Megamalai (High Wavy Mountains)",
            desc: "Megamalai is a hidden hill station in the Western Ghats known for tea plantations, misty hills, and untouched natural beauty. It is a perfect offbeat road trip destination from Madurai."
        },
        {
            image: "/assets/thekkady.jpg",
            title: "Thekkady (Periyar Wildlife Sanctuary)",
            desc: "Thekkady is famous for the Periyar Wildlife Sanctuary where visitors can enjoy boat safaris and spot elephants, deer, and exotic birds. It is also known for its spice plantations and lush forests."
        },
        {
            image: "/assets/suruli-falls.jpg",
            title: "Suruli Falls",
            desc: "Suruli Falls is a beautiful two-stage waterfall located in the Western Ghats near Cumbum. Surrounded by forest and hills, it is a refreshing nature getaway from Madurai."
        },
    ];

    const nearplaces = [
        {
            image: "/assets/meenakshi.jpg",
            title: "Meenakshi Amman Temple (2 km)",
            desc: "Meenakshi Amman Temple is the most iconic landmark of Madurai, famous for its magnificent Dravidian architecture, colorful gopurams, and rich history. The temple attracts millions of devotees and tourists every year.",
        },
        {
            image: "/assets/thirumalainayak.jpg",
            title: "Thirumalai Nayakkar Mahal (2 km)",
            desc: "Thirumalai Nayakkar Mahal is a 17th-century palace built by King Thirumalai Nayak. Known for its grand pillars and Indo-Saracenic architecture, it is one of the most popular historical attractions in Madurai.",
        },
        {
            image: "/assets/alagarkovil.jpg",
            title: "Alagar Kovil (21 km)",
            desc: "Alagar Kovil is a beautiful temple dedicated to Lord Vishnu located in the Alagar Hills near Madurai. Surrounded by scenic greenery, it is both a spiritual destination and a peaceful getaway.",
        },
        {
            image: "/assets/vaigaidam.jpg",
            title: "Vaigai Dam (70 km)",
            desc: "Vaigai Dam near Theni is a scenic spot surrounded by hills and gardens. It is a peaceful destination for families and a relaxing stop during road trips from Madurai."
        },
        {
            image: "/assets/pazhamudhircholai.jpg",
            title: "Pazhamudhircholai Murugan Temple (25 km)",
            desc: "Pazhamudhircholai is one of the six sacred abodes of Lord Murugan located in the lush Alagar Hills near Madurai. Surrounded by dense forest and scenic hills, it is both a spiritual destination and a beautiful nature getaway."
        }
    ];

    const whyCards = [
        {
            title: "Easy Booking in Madurai",
            text: "Book your self-drive car in Madurai within minutes and start your journey smoothly across the city or beyond.",
        },
        {
            title: "Safe & Well-Maintained Cars",
            text: "Our vehicles are regularly serviced and sanitized, ensuring a safe and comfortable drive around Madurai and Tamil Nadu.",
        },
        {
            title: "Flexible Rental Plans",
            text: "Choose hourly, daily, or outstation packages perfect for temple visits, business trips, or weekend getaways.",
        },
        {
            title: "Explore Madurai & Nearby Getaways",
            text: "Drive to Marudhamalai Temple, Adiyogi Shiva Statue, Siruvani Waterfalls, Valparai, or Ooty with complete freedom and comfort."
        },
        {
            title: "Airport & Railway Station Pickup",
            text: "Convenient car delivery at Madurai International Airport, Madurai Junction railway station, or your doorstep – anytime you need."
        }
    ];

    // Slider settings
    const sliderSettings = {
        dots: true,
        infinite: true,
        speed: 500,
        autoplay: true,
        autoplaySpeed: 2500,
        slidesToShow: 3,
        slidesToScroll: 1,
        responsive: [
            {
                breakpoint: 1024,
                settings: { slidesToShow: 2 },
            },
            {
                breakpoint: 768,
                settings: { slidesToShow: 1, arrows: false },
            },
        ],
    };

    return (
        <div className="min-h-screen bg-gray-50 text-gray-800 font-inter">
            {/* Header */}
            <header className="bg-yellow-100 shadow-sm sticky top-0 z-20">
                <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between">
                    <div className="flex-shrink-0">
                        <img
                            onClick={() => navigate("/")}
                            loading="lazy"
                            style={{ height: 55, width: 160 }}
                            src="https://bagzpack.s3.amazonaws.com/logo/68ea80da-f61b-4333-a4ed-ebae07c63893/IMG/1645694302682.png"
                            alt="wowcarzlogo"
                            className="object-cover cursor-pointer"
                        />
                    </div>

                    <div className="flex items-center gap-3">
                        <a
                            href="/"
                            className="bg-yellow-500 text-white px-4 py-2 rounded-lg shadow hover:bg-yellow-700 transition"
                        >
                            Book Now
                        </a>
                        <a
                            href="tel:+919844300066"
                            className="inline-flex items-center gap-2 border border-gray-200 px-3 py-2 rounded-lg text-sm hover:bg-gray-100 transition"
                        >
                            📞 Call Us
                        </a>
                    </div>
                </div>
            </header>

            <main className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                {/* Hero */}
                <section className="relative rounded-2xl h-[500px] sm:h-[550px] lg:h-[600px] overflow-hidden">

                    <img
                        src="/assets/mad.jpg"
                        alt="Self Drive Cars Chennai"
                        className="absolute inset-0 w-full h-full object-contain scale-10"
                    />
                    <div className="absolute inset-0 bg-black/50 flex flex-col justify-center items-center text-center px-6 sm:px-12 ">
                        <h2 className="text-3xl sm:text-5xl font-bold text-white mb-4 animate-fade-in">
                            Self Drive Cars in Madurai
                        </h2>

                        <p className="text-gray-200 text-base sm:text-lg mb-6 max-w-xl leading-relaxed">
                            Explore Madurai and nearby destinations with hassle-free self drive
                            cars from Wow Carz.
                        </p>
                    </div>

                </section>


                {/* Explore Section */}
                <section className="bg-yellow-200 p-5 m-5">
                    <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-3xl p-8 sm:p-12 text-center">
                        <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-6">
                            Explore Madurai with Wow Carz
                        </h2>
                        <p className="text-gray-700 text-base sm:text-lg leading-relaxed">
                            Explore Madurai with the comfort and freedom of a self drive car rental designed for modern travelers. Wow Carz offers reliable and premium self drive cars in Madurai, making it easy to travel across the city or plan a relaxing getaway whenever you want.

                            Perfect for business travel, family outings, weekend road trips, and short trips within 100 km, Wow Carz provides flexible self drive car rental in Madurai without the hassle of fixed schedules or driver dependency. Simply pick your car, start your journey, and enjoy complete travel freedom.

                            Choose Wow Carz for affordable, safe, and convenient self drive car rentals in Madurai and experience the joy of exploring the city and nearby destinations at your own pace.
                        </p>
                    </div>
                </section>

                {/* Why Rent Section (Slider) */}
                <section className="py-12 bg-gray-50">
                    <h2 className="text-3xl sm:text-4xl font-bold text-center mb-10 text-gray-900">
                        Why Rent With Wow Carz?
                    </h2>
                    <Slider {...sliderSettings} className="px-6 [&_.slick-dots]:left-0">
                        {whyCards.map((card, i) => (
                            <div key={i} className="px-3">
                                <div className="bg-white rounded-2xl shadow-lg p-6 text-center hover:shadow-xl transition">
                                    <h3 className="text-xl font-semibold mb-2">{card.title}</h3>
                                    <p className="text-gray-600 text-sm">{card.text}</p>
                                </div>
                            </div>
                        ))}
                    </Slider>
                </section>



                {/* Plan Your Trip */}

                <section className="py-12 sm:py-16 bg-yellow-300 mx-4 sm:mx-8 lg:mx-16 rounded-2xl">
                    <div className="max-w-7xl mx-auto text-center px-4">

                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-10 text-gray-900">
                            Plan Your Trip to Madurai
                        </h2>

                        {/* Desktop Grid */}
                        <div className="hidden sm:grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">

                            {tripCards.map((card, i) => (
                                <div key={i} className="bg-white p-6 sm:p-8 rounded-2xl shadow-md hover:shadow-xl transition duration-300">

                                    <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-purple-100 text-purple-600 text-2xl">
                                        {card.icon}
                                    </div>

                                    <h3 className="text-lg sm:text-xl font-semibold mb-3">
                                        {card.title}
                                    </h3>

                                    <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                                        {card.desc}
                                    </p>

                                </div>
                            ))}

                        </div>

                        {/* Mobile Carousel */}
                        <div className="block sm:hidden">
                            <Slider {...sliderSettings}>

                                {tripCards.map((card, i) => (
                                    <div key={i} className="px-3">
                                        <div className="bg-white p-6 rounded-2xl shadow-md text-center">

                                            <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-purple-100 text-purple-600 text-2xl">
                                                {card.icon}
                                            </div>

                                            <h3 className="text-lg font-semibold mb-3">
                                                {card.title}
                                            </h3>

                                            <p className="text-gray-700 text-sm leading-relaxed">
                                                {card.desc}
                                            </p>

                                        </div>
                                    </div>
                                ))}

                            </Slider>
                        </div>

                    </div>
                </section>


                {/* Near Places Section (Slider) */}
                <section className="mt-12">
                    <h3 className="text-2xl font-semibold text-gray-900 mb-2 text-center">
                        Places Near by Madurai
                    </h3>
                    <p className="text-gray-600 mb-6 text-center">
                        Perfect for day trips and weekend getaways with your Wow Carz ride.
                    </p>
                    <Slider {...sliderSettings} className="px-6 [&_.slick-dots]:left-0">
                        {nearplaces.map((place) => (
                            <div key={nearplaces.title} className="px-3">
                                <article className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl border border-gray-100 text-center transition">
                                    <img
                                        className="w-48 h-48 object-cover rounded-xl mx-auto mb-4"
                                        src={place.image}
                                        alt={place.title}
                                    />
                                    <h4 className="font-semibold text-lg mb-2">{place.title}</h4>
                                    <p className="text-gray-600 text-sm leading-relaxed">
                                        {place.desc}
                                    </p>
                                </article>
                            </div>
                        ))}
                    </Slider>
                </section>




                {/* Top Places */}
                <section className="mt-16">
                    <h3 className="text-2xl font-semibold text-gray-900 mb-2 text-center">
                        Top Places to Visit in and Around Madurai
                    </h3>
                    <p className="text-gray-600 mb-6 text-center">
                        Explore iconic landmarks, serene gardens, and historic towns with
                        your Wow Carz ride.
                    </p>
                    {/* Top place(PC view)*/}
                    <div className="hidden lg:grid grid-cols-3 gap-8 px-4 max-w-6xl mx-auto">
                        {places.map((place) => (
                            <div
                                key={place.title}
                                className="bg-white rounded-xl shadow-md hover:shadow-xl transition p-6 text-center"
                            >
                                <img
                                    src={place.image}
                                    alt={place.title}
                                    className="w-full h-56 object-cover rounded-lg mb-4"
                                />

                                <h4 className="text-xl font-semibold text-gray-900 mb-2">
                                    {place.title}
                                </h4>

                                <p className="text-gray-600 text-sm">
                                    {place.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                    {/* Top place(Mobile view)*/}
                    <div className="block lg:hidden max-w-xl mx-auto">
                        <Slider {...sliderSettings}>
                            {places.map((place) => (
                                <div key={place.title} className="px-3">
                                    <div className="bg-white rounded-xl shadow-md p-6 text-center">

                                        <img
                                            src={place.image}
                                            alt={place.title}
                                            className="w-full h-52 object-cover rounded-lg mb-4"
                                        />

                                        <h4 className="text-lg font-semibold text-gray-900 mb-2">
                                            {place.title}
                                        </h4>

                                        <p className="text-gray-600 text-sm">
                                            {place.desc}
                                        </p>

                                    </div>
                                </div>
                            ))}
                        </Slider>
                    </div>

                </section>




                {/* FAQ */}
                <section className="mt-16">
                    <h3 className="text-2xl font-semibold text-gray-900 mb-4 text-center">
                        Frequently Asked Questions
                    </h3>
                    <div className="space-y-4">
                        {faqs.map((f, i) => (
                            <div
                                key={i}
                                className="border rounded-xl bg-white shadow-sm overflow-hidden"
                            >
                                <button
                                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                                    className="w-full text-left px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition"
                                >
                                    <span className="font-medium text-gray-800">{f.q}</span>
                                    <span className="text-gray-500 text-lg">
                                        {openFaq === i ? "−" : "+"}
                                    </span>
                                </button>
                                <div
                                    className={`px-5 overflow-hidden transition-all duration-300 ${openFaq === i ? "max-h-40 py-3" : "max-h-0"
                                        }`}
                                >
                                    <p className="text-gray-700 text-sm leading-relaxed">{f.a}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* CTA */}
                <section className="mt-16 bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 text-white p-8 rounded-3xl flex flex-col sm:flex-row items-center justify-between gap-6">
                    <div className="text-center sm:text-left">
                        <h4 className="text-2xl font-semibold">
                            Ready to Start Your Journey?
                        </h4>
                        <p className="text-sm mt-2 text-blue-100">
                            Experience hassle-free driving with Wow Carz today.
                        </p>
                    </div>
                    <div className="flex gap-3">
                        <a
                            href="/"
                            className="bg-white text-blue-600 px-6 py-3 rounded-lg font-medium hover:bg-blue-100 transition"
                        >
                            Book Now
                        </a>
                        <a
                            href="tel:+919844300066"
                            className="border border-white px-6 py-3 rounded-lg hover:bg-white hover:text-blue-600 transition"
                        >
                            Call Us
                        </a>
                    </div>
                </section>

                {/* Footer */}
                <footer className="mt-10 text-center text-sm text-gray-500">
                    © {new Date().getFullYear()} Wow Carz — All rights reserved
                </footer>
            </main>
        </div>
    );
}

export default Madurai;
